import { useEffect, useMemo } from "react";
import { Link } from "wouter";
import { ArrowRight } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, SectionTitle } from "@/components/Card";
import { useDemoStore } from "@/store/useDemoStore";
import { useCandidatesList, useEffectiveUser, useExpedientesForEvaluator, useUsersByRole } from "@/store/hooks";

export default function EvaluatorHistory() {
  const setRole = useDemoStore((s) => s.setRole);
  useEffect(() => setRole("evaluador"), [setRole]);

  const me = useEffectiveUser();
  const evaluators = useUsersByRole("evaluador");
  const evalId = (me?.roles.includes("evaluador") ? me.id : evaluators[0]?.id) ?? "";

  const assignments = useExpedientesForEvaluator(evalId);
  const candidates = useCandidatesList();
  const scoresByExp = useDemoStore((s) => s.practicalScoresByExpediente);
  const preByExp = useDemoStore((s) => s.prechecklistByExpediente);

  const rows = useMemo(() => {
    return assignments
      .filter(({ exp }) => exp.evaluacionPractica.aplicada || preByExp[exp.id]?.completedAt)
      .map(({ exp }) => {
        const cand = candidates.find((c) => c.id === exp.candidatoId);
        const scores = scoresByExp[exp.id] ?? {};
        const total = exp.evaluacionPractica.criteriosTotales || Object.keys(scores).length;
        const comp = exp.evaluacionPractica.criteriosCompetente;
        return { exp, cand, total, comp, pre: preByExp[exp.id] };
      })
      .sort((a, b) => (b.exp.evaluacionPractica.fecha ?? "").localeCompare(a.exp.evaluacionPractica.fecha ?? ""));
  }, [assignments, candidates, scoresByExp, preByExp]);

  return (
    <AppLayout pageTitle="Mis evaluaciones">
      <Card>
        <SectionTitle>Historial de evaluaciones aplicadas</SectionTitle>
        {rows.length === 0 && (
          <div style={{ padding: 20, textAlign: "center", color: "var(--cc-gray-500)", fontSize: 13 }}>
            Aún no has aplicado evaluaciones.
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {rows.map(({ exp, cand, total, comp, pre }) => (
            <Link key={exp.id} href={`/evaluador/evaluacion/${exp.id}`} style={{ textDecoration: "none" }} data-testid={`row-history-${exp.id}`}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 12, border: "1px solid var(--cc-gray-100)", borderRadius: 10, cursor: "pointer" }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: cand?.avatarColor, color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700 }}>{cand?.iniciales}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--cc-navy)" }}>{cand?.nombre ?? exp.candidatoId}</div>
                  <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>
                    {exp.estandar} · Práctica {exp.evaluacionPractica.aplicada ? `${comp}/${total} competente` : "no aplicada"} · Preop. {pre?.completedAt ?? "—"} · Aplicada {exp.evaluacionPractica.fecha}
                  </div>
                </div>
                <Badge tone={exp.evaluacionPractica.aplicada ? "green" : "amber"}>
                  {exp.evaluacionPractica.aplicada ? "Aplicada" : "En curso"}
                </Badge>
                <ArrowRight size={16} color="var(--cc-gray-400)" />
              </div>
            </Link>
          ))}
        </div>
      </Card>
    </AppLayout>
  );
}
